import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react'

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const location = useLocation()

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Financial Services', href: '/financial-services' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' },
  ]

  const isActive = (href) => location.pathname === href

  return (
    <>
      {/* Top Bar */}
      <div className="bg-primary-800 text-white py-1">
        <div className="container-max flex justify-between items-center text-xs">
          <div className="flex items-center gap-6">
            <a href="tel:+917989308337" className="flex items-center gap-2 hover:text-accent-300 transition-colors">
              <Phone size={14} />
              <span className="text-sm">+91 7989308337</span>
            </a>
            <a href="mailto:info@shrijaaoutsourcing.com" className="hidden md:flex items-center gap-2 hover:text-accent-300 transition-colors">
              <Mail size={14} />
              <span className="text-sm">info@shrijaaoutsourcing.com</span>
            </a>
          </div>
          <a 
            href="https://wa.me/917989308337" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 px-4 py-1 rounded text-sm font-medium transition-colors"
          >
            WhatsApp Us
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="container-max">
          <div className="flex justify-between items-center h-14 md:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/shrijaa-logo.png" alt="Shrijaa Outsourcing Consultants" className="h-20 md:h-24 w-auto -my-4" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => (
                <div 
                  key={item.name} 
                  className="relative"
                  onMouseEnter={() => item.children && setOpenDropdown(item.name)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 flex items-center gap-1 ${
                      item.name === 'Financial Services'
                        ? isActive(item.href)
                          ? 'text-white bg-accent-600 shadow-sm'
                          : 'text-accent-600 bg-accent-50 hover:bg-accent-100 border border-accent-200 font-semibold shadow-sm'
                        : isActive(item.href) 
                          ? 'text-primary-600 bg-primary-50' 
                          : 'text-secondary-700 hover:text-primary-600 hover:bg-primary-50'
                    }`}
                  >
                    {item.name}
                    {item.children && <ChevronDown size={14} className="mt-0.5" />}
                  </Link>
                  
                  {/* Dropdown */}
                  {item.children && openDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      className="absolute top-full left-0 mt-0 bg-white rounded-lg shadow-xl border border-secondary-100 py-2 min-w-[220px] z-50"
                    >
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className="block px-4 py-2.5 text-sm text-secondary-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                        >
                          {child.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link
                to="/contact"
                className="btn-primary text-sm"
              >
                Get In Touch →
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-secondary-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white border-t border-secondary-100 absolute left-0 right-0 top-full shadow-lg"
            >
              <div className="container-max py-4">
                <div className="flex flex-col space-y-1">
                  {navigation.map((item) => (
                    <div key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block py-2.5 px-4 text-sm font-medium rounded-md transition-colors ${
                          item.name === 'Financial Services'
                            ? isActive(item.href)
                              ? 'text-white bg-accent-600 shadow-sm'
                              : 'text-accent-600 bg-accent-50 hover:bg-accent-100 border border-accent-200 font-semibold'
                            : isActive(item.href) 
                              ? 'text-primary-600 bg-primary-50' 
                              : 'text-secondary-700 hover:text-primary-600 hover:bg-primary-50'
                        }`}
                      >
                        {item.name}
                      </Link>
                      {item.children && (
                        <div className="ml-4 border-l-2 border-primary-100 pl-4 space-y-1 mt-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="block py-2 text-sm text-secondary-500 hover:text-primary-600 transition-colors"
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="pt-4 border-t border-secondary-100 mt-2">
                    <Link
                      to="/contact"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="btn-primary w-full text-center block text-sm"
                    >
                      Get In Touch →
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  )
}

export default Header
